/**
 * Cálculo determinístico de pose e posicionamento interposto da
 * Proteção Rígida (Lágrima) no salvamento veicular.
 *
 * Doutrina operacional (CBMSC / CBMERJ / CBMESP / NFPA 1006):
 * O socorrista interno ou de apoio posiciona a proteção rígida (Lágrima)
 * diretamente no espaço intermediário entre o corpo da vítima (cabeça/tronco)
 * e o ponto de risco (ferramenta hidráulica, lâmina do cortador, cunha do
 * alargador ou vidro sendo rompido), atuando como barreira física
 * impenetrável contra projeção de estilhaços e cisalhamento mecânico.
 */

export interface PoseLagrima {
  visivel: boolean
  posicao: [number, number, number]
  rotacao: [number, number, number]
  escala: number
}

export interface ParametrosPoseLagrima {
  /** Posição vital de referência da vítima (cabeça / tórax). */
  posicaoVitima: [number, number, number]
  /** Ponto de intervenção ativa (ponta da ferramenta ou centro do vidro). */
  posicaoPerigo: [number, number, number]
  /** Lado da intervenção em relação ao veículo. */
  lado?: 'esquerdo' | 'direito'
  /** Proporção do vetor vítima -> perigo para apoiar o escudo (padrão 0.54). */
  fatorInterposto?: number
}

/** Raio máximo em metros dentro do qual uma intervenção é considerada próxima à vítima. */
export const RAIO_MAXIMO_PROXIMIDADE_METROS = 2.4

/** Distância livre mínima entre o centro de referência da vítima e o escudo. */
export const DISTANCIA_MINIMA_LAGRIMA_VITIMA_METROS = 0.38

/**
 * Avalia se a vítima está próxima o bastante da intervenção para exigir
 * barreira física da Proteção Rígida.
 */
export function vitimaEstaProxima(
  posicaoVitima: [number, number, number],
  posicaoPerigo: [number, number, number],
  raioMaximo = RAIO_MAXIMO_PROXIMIDADE_METROS,
): boolean {
  const dx = posicaoPerigo[0] - posicaoVitima[0]
  const dy = posicaoPerigo[1] - posicaoVitima[1]
  const dz = posicaoPerigo[2] - posicaoVitima[2]
  const distancia = Math.hypot(dx, dy, dz)
  return distancia <= raioMaximo && distancia > 0.05
}

/**
 * Calcula a posição e rotação da Proteção Rígida (Lágrima) interposta entre
 * a vítima e a região do corte ou quebra de vidro.
 */
export function calcularPoseLagrima({
  posicaoVitima,
  posicaoPerigo,
  lado,
  fatorInterposto = 0.54,
}: ParametrosPoseLagrima): PoseLagrima {
  const dx = posicaoPerigo[0] - posicaoVitima[0]
  const dy = posicaoPerigo[1] - posicaoVitima[1]
  const dz = posicaoPerigo[2] - posicaoVitima[2]
  const distPlana = Math.hypot(dx, dz)

  if (distPlana < 0.02) {
    return {
      visivel: false,
      posicao: posicaoVitima,
      rotacao: [0, 0, 0],
      escala: 1,
    }
  }

  // A lâmina fica de frente para o perigo, portanto sua espessura acompanha
  // o eixo vítima→perigo. Preserve espaço para o tronco antes de aplicar a
  // fração interposta; em cortes muito próximos, a proteção fica junto ao
  // perigo para priorizar a separação da vítima.
  const distanciaHorizontal = Math.hypot(dx, dz)
  const fatorMinimo = DISTANCIA_MINIMA_LAGRIMA_VITIMA_METROS / distanciaHorizontal
  const fatorPosicao = Math.max(fatorInterposto, fatorMinimo)
  const px = posicaoVitima[0] + dx * fatorPosicao
  // Mantém a altura da lágrima no horizonte de proteção do tronco/cabeça
  const alturaBase = posicaoVitima[1] + dy * fatorPosicao
  const py = Math.max(0.60, Math.min(1.08, alturaBase))
  const pz = posicaoVitima[2] + dz * fatorPosicao

  // Ângulo horizontal (yaw): a face frontal (normal +Z) aponta para o perigo
  const yaw = Math.atan2(dx, dz)

  // Inclinação vertical suave (pitch) acompanhando a diferença de altura
  const pitch = -Math.atan2(dy, distPlana) * 0.25

  // Roll sutil conforme o lado para conformar o bojo anatômico à guarnição interna
  const roll = lado === 'direito' ? -0.08 : 0.08

  return {
    visivel: true,
    posicao: [px, py, pz],
    rotacao: [pitch, yaw, roll],
    escala: 1,
  }
}
